(ns frontend.view.grid.tasks-grid.view
  (:require-macros [frontend.macros :refer [without-default-action-and-propagation]])
  (:require
   [clojure.string :as string]
   [common.attribute :as attribute]
   [common.fields.table-column-widths :as widths]
   [common.localization :as loc]
   [common.schema.registry :as schema-registry]
   [common.util :as cu]
   [frontend.db :as app-db]
   [frontend.page-components.default-page-layout :as default-page-layout]
   [frontend.page-components.zap-toolbar :as zap-toolbar]
   [frontend.react.virtualized.scrollable-grid.common :as grid]
   [frontend.react.virtualized.scrollable-grid.view :as scrollable-grid]
   [frontend.react.virtualized.utils :as vu]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.tenant-customizations :as tenant-customizations]
   [frontend.utils :as fu]
   [frontend.ux-overhaul.components.filter.view :as filter]
   [frontend.ux-overhaul.kanban.view :as kanban]
   [frontend.ux-overhaul.listing.task.view :as task-listing]
   [frontend.view.components.sidenavs.named-views.view :as named-views]
   [frontend.view.curtains.webentry-resolution.view :as webentry-resolution-curtain]
   [frontend.view.grid.common.components :as components]
   [frontend.view.grid.common.no-results-view :as no-results-view]
   [frontend.view.grid.common.utils :as grid-utils]
   [frontend.view.grid.tasks-grid.db :as db]
   [frontend.view.grid.tasks-grid.events :as events]
   [frontend.view.grid.tasks-grid.subs :as subs]
   [frontend.view.modals.activity-entity-editor.view :as activity-entity-editor]
   [frontend.view.modals.bulk-edit-entity.view :as bulk-edit-modal]
   [frontend.view.modals.events :as modals]
   [frontend.view.not-found :as not-found]
   [frontend.zap-time :as zap-time]
   [potpuri.core :as potpuri]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.radix-ui.dropdown-menu.view :as dropdown-menu]
   [ui.components.rich-text.tiptap :as tiptap]
   [ui.util :as uu]))

(defonce select-width 30)

(defn column
  [args]
  (let [{:keys [label attribute filter-data sorting-disabled? width hover-atom recompute-grid-sizes!
                flex-grow]}
        args
        default-width (-> (widths/get-column-width attribute)
                          :default-width
                          (or widths/default-width))]
    {:label               label
     :data-key            attribute
     :filter-data         filter-data
     :update-filter!      (fn [k filter-data]
                            (grid/set-filter (events/setup-args) k filter-data))
     :sort-disabled?      (boolean sorting-disabled?)
     :min-width           (-> (widths/get-column-width attribute)
                              :min-width
                              (or widths/min-width))
     :max-width           (-> (widths/get-column-width attribute)
                              :max-width
                              (or widths/max-width))
     :default-width       default-width
     :width               (or width default-width)
     :set-width           width
     :begin-width-update! (partial grid/begin-width-update! hover-atom attribute)
     :update-width!
     (partial grid/update-width! (events/setup-args) recompute-grid-sizes! hover-atom attribute)
     :end-width-update!   (partial grid/end-width-update! hover-atom attribute)
     :get-styling         (partial grid/get-styling hover-atom attribute)
     :flex-grow           flex-grow}))

(defn system-generated-task?
  [task]
  (contains? #{:task.type/email :task.type/webentry} (:task/type task)))

(defn todo-button
  [_]
  (let [state (r/atom {:text (loc/tr :to-do)})
        mouse-over (fn [system-generated? _]
                     (if system-generated?
                       (swap! state assoc :text (loc/tr :resolve))
                       (swap! state assoc :text (loc/tr :done))))
        mouse-out (fn [_]
                    (swap! state assoc :text (loc/tr :to-do)))]
    (fn [task]
      (let [type (:task/type task)
            system-generated? (system-generated-task? task)]
        [button/sm-warning-solid
         {:on-mouse-over (partial mouse-over system-generated?)
          :on-mouse-out  mouse-out
          :on-click      (without-default-action-and-propagation
                          (case type
                            :task.type/webentry (events/handle-webentry task)
                            :task.type/email (events/handle-email task)
                            (events/complete-task! task)))}
         [:span (:text @state)]]))))

(defn completed-button
  [_]
  (let [state (r/atom {:text     (loc/tr :done)
                       :tooltip? false})
        mouse-over (fn [system-generated? _]
                     (if-not system-generated?
                       (swap! state assoc :text (loc/tr :to-do))
                       (swap! state assoc :tooltip? true)))
        mouse-out (fn [_]
                    (swap! state assoc
                           :text (loc/tr :done)
                           :tooltip? false))]
    (fn [task]
      (let [system-generated? (system-generated-task? task)
            tooltip (loc/tr :cannot-be-undone)]
        [button/sm-primary-solid
         {:disabled      system-generated?
          :tooltip       (when system-generated?
                           tooltip)
          :on-mouse-over (partial mouse-over system-generated?)
          :on-mouse-out  mouse-out
          :on-click      (without-default-action-and-propagation
                          (events/reactivate-task! task))}
         [:span
          ;;prevent click from bubbling up when button is disabled
          {:on-click (when system-generated?
                       (without-default-action-and-propagation identity))}
          (:text @state)]]))))

(defn status-cell
  [task]
  (if (:task/completed? task)
    [completed-button task]
    [todo-button task]))

(defn due-cell
  [task]
  (let [due-ts (:task/due task)
        due (:task/due (db/get-tasks-display-data task))]
    (if (<= due-ts @db/server-time)
      [:span.due-today due]
      [:span.due due])))

(defn toolbar-actions
  [_state]
  [#_{:toolbar-button?  true                                                                        ;;TODO later
      :dropdown-action? true
      :ord              2
      :primary-button?  false
      :on-select        (fn [e]
                          (grid/open-select-columns-curtain (events/setup-args)))
      :label            (loc/tr :action/select-columns)}
   #_(when (and (not (session/is-mobile?))
                (permissions/allow-export? @session/tenant @session/role))
       {:toolbar-button?  true                                                                      ;;TODO later
        :dropdown-action? true
        :ord              3
        :primary-button?  false
        :on-select        (fn [e]
                            (events/export-data))
        :label            (loc/tr :action/export)})

   (when-not (session/is-read-only-user?)
     {:toolbar-button?  true
      :ord              1
      :icon             :fa-plus
      :primary-button?  true
      :on-select        (fn [_e]
                          (modals/open-modal! activity-entity-editor/modal-key
                                              {:new-entity-type :entity.type/task}))
      :label            (loc/tr :task-add-button)})
   (grid-utils/preferred-landing-page-toggle {:view-id :tasks-grid
                                              :selected-task-tab (:tasks-view @db/tasks)})])

(defn toolbar-buttons-renderer
  []
  (let [setup-args (events/setup-args)
        modal-atom (:modal-state setup-args)
        open-bulk-edit! #(swap! modal-atom assoc :bulk-edit-modal true)
        selected (grid/get-selected-items setup-args)]
    [:div
     {:class "flex gap-2"}
     (if (session/is-read-only-user?)
       [:span (loc/tr :no-grid-actions-for-read-only-user)]
       [:<>
        [components/toolbar-delete-button
         {:max-delete-count   10000
          :selected-items     (grid/get-selected-items setup-args)
          :on-delete-clicked! events/delete-selected-clicked}]
        [components/toolbar-bulk-edit-button
         {:max-bulk-edit-count   10000
          :selected-items        selected
          :on-bulk-edit-clicked! open-bulk-edit!}]])]))

(defn create-props-atom
  [selected sorted-grid-entities filters grid-store
   recompute-grid-sizes! hover-atom modal-atom]
  (let [tenant-schema @session/tenant-schema
        columns
        (vec (keep (fn [k]
                     (let [field-schema (schema-registry/get-field-schema tenant-schema k)
                           label (attribute/attribute-label tenant-schema k)
                           data-type (:field/data-type field-schema)]
                       (when-not (= data-type :data.type/currency-symbol)
                         (column {:label                 label
                                  :attribute             k
                                  :field-schema          field-schema
                                  :filter-data           (get filters k)
                                  :sorting-disabled?     false
                                  :width                 (grid/get-saved-width (events/setup-args) k)
                                  :hover-atom            hover-atom
                                  :recompute-grid-sizes! recompute-grid-sizes!
                                  :flex-grow             1}))))
                   (events/resolve-column-keys)))]
    (r/atom
     {:grid-store              grid-store
      :setup-args              (events/setup-args)
      :grid-entity-ids         (->> sorted-grid-entities
                                    (mapv :db/id)
                                    (into #{}))
      :selected                selected
      ;:link-to-entity-field   nil
      :header-height           50
      :select-width            select-width
      :min-columns-width       30
      :height                  (- (or (:height @app-db/window) 400)
                                  75)                                                               ;; TODO: lazy - fixed 120px for button row in this view
      :avail-height            (- (or (:height @app-db/window) 400)
                                  75)                                                               ;; TODO: lazy - fixed 120px for button row in this view
      :useDynamicRowHeight     false                                                                ;; TODO: kebab-case
      :row-height              40                                                                   ;(fn [index] 40)
      :row-count               (if (sequential? sorted-grid-entities)
                                 (count sorted-grid-entities)
                                 0)
      :scrollToIndex           0
      :columns                 columns
      :overscan-count          1
      :no-rows-renderer        (fn []
                                 (r/as-element
                                  [:div.grid-no-rows
                                   (cond
                                     (not (:loaded @db/tasks))
                                     (loc/tr :grid-loading-data)

                                     (and (= :task.type/simple (:tasks-view @db/tasks))
                                          (->> @db/tasks
                                               :grid-entities
                                               (cu/filter-by :task/type :task.type/simple)
                                               (empty?)))
                                     [no-results-view/no-entities-view
                                      {:entity-type :entity.type/task
                                       :create-fn
                                       (fn []
                                         (modals/open-modal!
                                          activity-entity-editor/modal-key
                                          {:new-entity-type :entity.type/task}))}]

                                     :else
                                     [no-results-view/no-entities-found-view])]))
      :on-row-click!           (fn [row]
                                 (let [data (when (< row (count sorted-grid-entities))
                                              (nth sorted-grid-entities row))
                                       task-type (:task/type data)]
                                   (case task-type
                                     :task.type/webentry (events/handle-webentry data)
                                     :task.type/email (events/handle-email data)
                                     (modals/open-modal! activity-entity-editor/modal-key
                                                         {:entity-id (:db/id data)}))))
      :is-sorted-by?           (partial grid/is-sorted-by db/view-settings)
      :is-secondary-sorted-by? (partial grid/is-secondary-sorted-by db/view-settings)
      :sort-by!                (fn [k dir]
                                 (grid/set-sort-by (events/setup-args) k dir))
      :reorder-columns!        (fn [dragged-column after-column]
                                 (grid/reorder-columns! (events/setup-args)
                                                        dragged-column
                                                        after-column))
      :can-filter?             (fn [_k]
                                 false)
      :resizing-column         (some-> @hover-atom
                                       (get :resize-start-width)
                                       (first)
                                       (key))
      :recompute-grid-sizes!   recompute-grid-sizes!
      :all-selection!          (fn []
                                 (let [selected (:selected @db/tasks)]
                                   (if (or (= :all selected) (not-empty selected))
                                     (grid/clear-selection! (events/setup-args))
                                     (swap! db/tasks assoc :selected :all))))
      :select-action-renderer  toolbar-buttons-renderer
      :on-row-select!          (fn [index checked]
                                 (let [data (when (< index (count sorted-grid-entities))
                                              (nth sorted-grid-entities index))
                                       task-id (:db/id data)]
                                   (if (= selected :all)
                                     (grid/select-all-but (events/setup-args) task-id)
                                     (if (boolean checked)
                                       (swap! db/tasks assoc-in [:selected task-id] true)
                                       (swap! db/tasks potpuri/dissoc-in [:selected task-id])))))
      :deselect-entity!        (fn [entity-id]
                                 (if (= selected :all)
                                   (grid/select-all-but (events/setup-args) entity-id)
                                   (swap! db/tasks potpuri/dissoc-in [:selected entity-id])))
      :row-data-getter         (fn [index]
                                 (let [default-data (grid/default-row-data-getter
                                                     sorted-grid-entities
                                                     selected
                                                     index)]
                                   (assoc default-data
                                          :display-data
                                          {:task/message    (fu/html->plain-text (:task/message
                                                                                  default-data))
                                           :task/completed? [status-cell default-data]
                                           :task/due        [due-cell default-data]})))
      :new-action-view         {:in-use?         true
                                :open-bulk-edit! #(swap! modal-atom assoc :bulk-edit-modal true)
                                :bulk-delete!    events/delete-selected-clicked
                                :modal-atom      modal-atom}})))

(defn tab-counter
  [pending-tasks]
  (if (and pending-tasks
           (> (count pending-tasks) 0))
    (str "(" (count pending-tasks) ")")
    ""))

(def dropdown-actions
  [#_{:label     "Advanced filters"
    :icon      nil
    :disabled? true
    :ord       1
    :on-select #(js/console.log "Advanced filters")}
   {:label     (loc/tr :view-settings)
    :icon      nil
    :ord       2
    :on-select (fn []
                 (let [view-configuration @(rf/subscribe [::subs/view-configuration])]
                   (rf/dispatch
                    [:start-view-configuration
                     {:entity-type        :entity.type/task
                      :view-configuration view-configuration}])))}
   #_{:label     "Go to full screen"
    :disabled? true
    :ord       3
    :on-select #(js/console.log "go full screen")}
   #_{:label     "Save as excel"
    :disabled? true
    :ord       4
    :on-select #(js/console.log "save as excel")}
   #_{:label     "Save as excel with persons"
    :disabled? true
    :ord       5
    :on-select #(js/console.log "save as excel with persons")}])

(defn three-dots-dropdown
  []
  (let [dropdown-actions dropdown-actions]
    (r/as-element
     [dropdown-menu/view
      {:trigger [dropdown-menu/button-trigger-open
                 [:i.fas.fa-ellipsis-v]]
       :items   (->> dropdown-actions
                     (map-indexed
                      (fn [_idx action]
                        (let [{:keys [label icon divider divider?
                                      on-select disabled?]}
                              action]
                          {:label          label
                           :icon-class     (uu/cm "fal" icon)
                           :separator?     divider?
                           :disabled?      (boolean disabled?)
                           :section-label? (true? divider)
                           :on-click       on-select})))
                     (doall))}])))


(defn navigation-elems
  [_state]
  (let [my-unfinished-tasks (->> @session/all-tasks
                                 (remove :common/disabled?)
                                 (remove :task/completed?)
                                 (filter (fn [task]
                                           (= (:task/assignee task) (:db/id @session/account)))))
        unfinished-by-type (->> my-unfinished-tasks
                                (group-by :task/type))
        counter-by-type (fn [type]
                          (->> unfinished-by-type
                               type
                               tab-counter))
        email-text (loc/tr :tasks-toolbar-tab-email-tasks [(counter-by-type :task.type/email)])
        webentry-text (loc/tr :tasks-toolbar-tab-webentry-tasks [(counter-by-type :task.type/webentry)])
        simple-text (loc/tr :tasks-toolbar-tab-simple-tasks [(counter-by-type :task.type/simple)])
        selected-task-tab (:tasks-view @db/tasks)]
    [:div
     {:class "flex"}
     (when (and
            (not (session/is-read-only-user?))
            (or (session/dealflow?)
                (session/sales-pipelines?)
                (session/fundraising?)))
       [:<>
        [:span
         {:class-name (fu/cs "block p-2 text-base-light-text hover:text-base-light-text-accent cursor-pointer"
                             (when (= selected-task-tab :task.type/email)
                               "text-base-light-text-accent"))
          :on-click   #(events/set-tasks-view :task.type/email)}

         [:span
          {:class "md:invisible"}
          [:i.fal.fa-inbox
           {:class "md:invisible"}]]
         [:span
          {:class "invisible md:visible"}
          email-text]]
        [:span
         {:class-name (fu/cs "block p-2 text-base-light-text hover:text-base-light-text-accent cursor-pointer"
                             "zap-tasks-toolbar-webentry__handle"
                             (when (= selected-task-tab :task.type/webentry)
                               "text-base-light-text-accent"))
          :on-click   #(events/set-tasks-view :task.type/webentry)}
         [:span
          {:class "md:invisible"}
          [:i.fal.fa-browser]]
         [:span
          {:class "invisible md:visible"}
          webentry-text]]])
     [:span
      {:class-name (fu/cs "block p-2 text-base-light-text hover:text-base-light-text-accent cursor-pointer"
                          (when (= selected-task-tab :task.type/simple)
                            "text-base-light-text-accent"))
       :on-click   #(events/set-tasks-view :task.type/simple)}

      [:span
       {:class "md:invisible"}
       [:i.fal.fa-user-tag]]
      [:span
        {:class "invisible md:visible"}
       simple-text]]]))

(defn owner-circle
  [_entity owner]
  (let [pic-url (fu/only-https-or-relative-img (:account/picture-url owner))
        first-name (:account/first-name owner)
        last-name (:account/last-name owner)]

    [button/secondary-open
     {:tooltip  (str "Task owner: " first-name " " last-name)}
     [:div
      {:class "flex justify-center items-center
               w-6 h-6
               text-base-gloomy-text text-xs font-semibold
               border border-solid border-base-light-border-paler
               bg-base-gloomy rounded-full"}
      (if pic-url
        [:span
         {:class "block w-full h-full rounded-full bg-cover bg-no-repeat bg-center"
          :style {:background-image (str "url('" (fu/sanitize-img-url pic-url) "')")}}]
        [:span
         (string/trim (str (first (:account/first-name owner))
                           ""
                           (first (:account/last-name owner))))])]]))

(defn due-date
  [date-millis]
  (let [date-string (zap-time/long->formatted-date date-millis)]
    [button/secondary-open
     {:tooltip (str "Task due: " date-string)}
     [:div
      {:class "flex items-center gap-2 text-xs"}
      [:i.fal.fa-calendar]
      [:span
       date-string]]]))

(defn get-card-body-content
  [task body-content-attributes
   show-empty-fields?
   empty-field-placeholder]
  (let [display-values (db/get-tasks-display-data task)
        tenant-schema @session/tenant-schema
        content (->> body-content-attributes
                     (filterv (fn [attr]
                                (let [value (get display-values attr)]
                                  (if show-empty-fields?
                                    true
                                    (and value
                                         (not= value ""))))))
                     (mapv (fn [attr]
                             (let [value (get display-values attr)
                                   value (if (= attr :task/completed?)
                                           (if value (loc/tr :done) (loc/tr :to-do))
                                           (if (or (not value)
                                                   (= value ""))
                                             empty-field-placeholder
                                             value))]
                               {:tag   (attribute/attribute-label tenant-schema attr)
                                :value value}))))]
    content))

(defn get-card-width
  [card-width-kw]
  (condp = card-width-kw
    :narrow 200
    :wide 300
    :wider 400
    (do
      (js/console.warn "unknown card width")
      300)))


(defn task-kanban-card
  [{:keys [data]} card-settings]
  (let [card-height (-> card-settings
                        :look-and-feel
                        :card-height)
        card-width (-> card-settings
                       :look-and-feel
                       :card-width)

        due-date-millis (:task/due data)

        task-owner (-> data
                       :task/assignee
                       (session/get-entity))

        task-type (:task/type data)

        header-copy (condp = task-type
                      :task.type/simple (or (:task/title data) "")
                      :task.type/webentry "Resolve form submission"
                      :task.type/email "Resolve email message"
                      "")

        on-card-click (fn []
                        (case task-type
                          :task.type/webentry (events/handle-webentry data)
                          :task.type/email (events/handle-email data)
                          (modals/open-modal! activity-entity-editor/modal-key
                                              {:entity-id (:db/id data)})))

        body-content-attributes (->> card-settings
                                     :card-body
                                     :content
                                     (filterv (fn [attr]
                                                (if (and (not= task-type :task.type/simple)
                                                         (= attr :task/due))
                                                  false
                                                  true))))

        show-empty-fields? (-> card-settings
                               :look-and-feel
                               :show-empty-fields?)

        empty-field-placeholder (-> card-settings
                                    :look-and-feel
                                    :empty-field-placeholder)

        body-content (get-card-body-content
                      data
                      body-content-attributes
                      show-empty-fields?
                      empty-field-placeholder)

        show-footer? (-> card-settings
                         :card-footer
                         :show-footer?)]
    [:div
     {:class    (uu/cm "flex flex-col justify-between bg-base-light text rounded")
      :on-click on-card-click}
     [:div
      {:class "px-3 py-2 flex"}
      [:p {:class "truncate"}
       header-copy]]

     (when-not (and (= card-height :compact)
                    show-footer?)
       [:div
        {:class "p-3 relative"}
        [:div
         {:class "flex flex-col gap-2 max-h-full overflow-hidden"}
         (->> body-content
              (map-indexed (fn [idx item]
                             (let [tag (:tag item)
                                   value (:value item)]
                               ^{:key (str "item-" idx)}
                               [:div
                                {:class "flex flex-col gap-1"}
                                [:p {:class "text-xs text-base-light-text-paler"}
                                 (:tag item)]
                                (if (= tag "Task description")
                                  [:div
                                   {:class "*:!p-0 *:!text-sm [&_.ProseMirror]:my-0"}
                                   [tiptap/viewer {:value value}]]
                                  [:p
                                   {:class "m-0 text-sm"}
                                   value])])))
              (doall))]
        (when-not (= card-height :dynamic)
          [:div {:class ""}])])

     (when show-footer?
       [:div
        {:class "pt-2 pb-3 px-3 flex justify-between items-center gap-2"}
        #_(if task-overdue? [task-overdue-icon] [:div])
        [:div
         {:class "*:!p-0"}
         (when-not (= card-width :narrow) [:div])

         (when due-date-millis
           [due-date due-date-millis])]

        [:div
         {:class "*:!p-0"}
         [owner-circle data task-owner]]])]))

(defn get-field-label
  [kw]
  (condp = kw
    :task/assignee "Owner"
    :task/completed? "Status"
    ""))

(defn get-field-value
  [field value]
  (condp = field
    :task/assignee (let [account (session/get-entity value)]
                     (str (:account/first-name account)
                          " "
                          (:account/last-name account)))
    :task/completed? (if value "Completed" "Pending")
    ""))

(defn filter-editor
  [{:keys [field
           on-field-change
           value
           on-value-change
           existing-filters
           on-save! on-cancel
           capman-not-admin?]}]
  (let [status-options [{:key   false
                         :label (loc/tr :pending)}
                        {:key   true
                         :label (loc/tr :completed)}]

        existing-filter-keys (->> existing-filters
                                  (mapv (fn [{:keys [field]}]
                                          field)))

        default-selected-field :task/assignee

        field (or field default-selected-field)

        field-options (concat []
                              (when-not capman-not-admin?
                                [{:key   :task/assignee
                                  :label (loc/tr :owner)}])
                              [{:key   :task/completed?
                                :label (loc/tr :status)}])

        field-options (->> field-options
                           (filterv (fn [{:keys [key]}]
                                      (if (= key field)
                                        true
                                        (not (some #{key} existing-filter-keys))))))

        owner-opts (->> (session/tenant-users)
                        (filterv (fn [acc]
                                   (not (:common/disabled? acc))))
                        (mapv (fn [acc]
                                (let [{:keys [account/first-name
                                              account/last-name
                                              db/id]}
                                      acc]
                                  {:key   id
                                   :label (str first-name " " last-name)}))))

        value-options (if (= field :task/assignee)
                        owner-opts
                        status-options)]
    [:div
     {:class "flex flex-row gap-2"}
     [:div
      {:class "flex justify-self-center"}
      [:select
       {:id        "field"
        :value     field
        :on-change on-field-change}
       (->> field-options
            (map-indexed (fn [index {:keys [key label]}]
                           ^{:key index}
                           [:option
                            {:value key}
                            label]
                           ))
            (doall))]]
     [:div
      {:class "flex justify-center"}
      [:select
       {:on-change on-value-change
        :value     value
        :style     {:min-width "120px"}}
       (->> value-options
            (map-indexed (fn [index {:keys [key label]}]
                           ^{:key index}
                           [:option
                            {:value key}
                            label]))
            (doall))]]

     [:div
      {:class "flex flex-row gap-2"}
      [button/sm-secondary-hollow
       {:on-click on-cancel}
       (loc/tr :cancel)]
      [button/sm-primary-solid
       {:on-click on-save!}
       (loc/tr :save)]]]))

(defn new-filters
  []
  (let [local-state (r/atom {:new-filter-editor-open? false
                             :existing-filter-editor  {}
                             :field                   ""
                             :value                   ""})

        filters-sub (rf/subscribe [::subs/task-filters])

        capman-not-admin? (and (tenant-customizations/capman?)
                               (not (session/is-admin?)))

        on-field-change (fn [e]
                          (let [value (.. e -target -value)
                                attribute (keyword (str "task/" value))]
                            (swap! local-state assoc :field attribute)
                            (swap! local-state assoc :value "")))

        on-value-change (fn [e]
                          (let [value (.. e -target -value)
                                clj-value (js->clj value)
                                new-value (if (= (:field @local-state) :task/completed?)
                                            (= "true" clj-value)
                                            (js/parseInt value))]
                            (swap! local-state assoc :value new-value)))

        on-cancel (fn [editor-type field]
                    (swap! local-state dissoc :field :value)
                    (if (= editor-type :new-filter-editor)
                      (swap! local-state dissoc
                             :new-filter-editor-open?)
                      (swap! local-state assoc-in
                             [:existing-filter-editor field]
                             false)))

        on-save! (fn [editor-type]
                   (let [field (:field @local-state)
                         value (:value @local-state)
                         prev-filters @filters-sub]
                     (if (= editor-type :new-filter-editor)
                       (do
                         (let [new-filters (conj prev-filters
                                                 {:field field
                                                  :value value})]
                           (rf/dispatch [::events/update-filters new-filters]))
                         (swap! local-state dissoc :value :field :new-filter-editor-open?))

                       (let [updated-filters (->> prev-filters
                                                  (mapv (fn [existing-filter]
                                                          (if (= (:field existing-filter)
                                                                 field)
                                                            (assoc existing-filter
                                                                   :value
                                                                   value)
                                                            existing-filter))))]
                         (rf/dispatch [::events/update-filters updated-filters])
                         (swap! local-state dissoc :field :value)
                         (swap! local-state assoc-in [:existing-filter-editor field] false)))))

        on-delete-filter (fn [removed-field]
                           (let [prev-filters @filters-sub
                                 new-filters (->> prev-filters
                                                  (filterv (fn [filter-obj]
                                                             (not= (:field filter-obj)
                                                                   removed-field))))]
                             (rf/dispatch [::events/update-filters new-filters])))

        get-new-filter-defaults (fn []
                                  (let [current-filters @filters-sub

                                        current-filter-keys
                                        (->> current-filters
                                             (mapv :field))

                                        current-user-id @session/account-id]
                                    (if capman-not-admin?
                                      {:field :task/completed?
                                       :value false}
                                      (if (some #{:task/assignee}
                                                current-filter-keys)
                                        {:field :task/completed?
                                         :value false}
                                        {:field :task/assignee
                                         :value current-user-id}))))]
    (fn []
      [:div
       {:class "flex gap-3 *:m-0 *:py-0 *:text-sm"}
       (->> @filters-sub
            (map-indexed
             (fn [index filter]
               (let [{:keys [field value]} filter
                     label (get-field-label field)
                     value-str (get-field-value field value)
                     edit-mode? (get-in @local-state [:existing-filter-editor field])]
                 ^{:key (str "filter-" index)}
                 [:div
                  (if edit-mode?
                    [filter-editor
                     {:field             field
                      :on-field-change   on-field-change
                      :value             (:value @local-state)
                      :on-value-change   on-value-change
                      :existing-filters  @filters-sub
                      :on-save!          (partial on-save!
                                                  :existing-filter-editor)
                      :on-cancel         (partial on-cancel
                                                  :existing-filter-editor
                                                  field)
                      :capman-not-admin? capman-not-admin?}]
                    [filter/filter-bubble
                     {:text       (str label ": " value-str)
                      :color      "black"
                      :selected?  false
                      :on-click!  (fn [] nil)
                      :on-edit!   (fn []
                                    (swap! local-state assoc
                                           :field field
                                           :value value)
                                    (swap! local-state assoc-in
                                           [:existing-filter-editor field]
                                           true))
                      :on-delete! (fn []
                                    (on-delete-filter field))}])])))
            (doall))

       (if (:new-filter-editor-open? @local-state)
         [filter-editor
          {:field             (:field @local-state)
           :on-field-change   on-field-change
           :value             (:value @local-state)
           :on-value-change   on-value-change
           :existing-filters  @filters-sub
           :on-save!          (partial on-save! :new-filter-editor)
           :on-cancel         (partial on-cancel :new-filter-editor)
           :capman-not-admin? capman-not-admin?}]
         (let [no-existing-filter-editors
               (->> (:existing-filter-editor @local-state)
                    vals
                    (mapv not)
                    (every? true?))

               show-button? (and no-existing-filter-editors
                                 (if capman-not-admin?
                                   (< (count @filters-sub) 1)                                       ; CapMan normal users can only apply one filter
                                   (< (count @filters-sub) 2)))]
           (when show-button?
             (let [label (if (= (count @filters-sub) 0)
                           (str (loc/tr :filter-by) "...")
                           (str (loc/tr :filter-more) "..."))]
               [button/secondary-open
                {:on-click (fn []
                             (let [{:keys [field value]} (get-new-filter-defaults)]
                               (swap! local-state assoc :field field :value value)
                               (swap! local-state assoc
                                      :new-filter-editor-open?
                                      true)))}
                label]))))])))

(defn new-toolbar
  [{:keys [state-atom]}]
  (let [view-mode (:mode @state-atom)]
    [:div
     {:class "px-4 py-2 flex items-start justify-between"}
     [:div.d-flex.align-items-center
      {:class "flex items-center gap-4"}
      [zap-toolbar/search-elems
       {:placeholder (loc/tr :search-for-a-task)
        :state       db/tasks
        :on-change   (fn [e]
                       (grid/set-universal-search! (events/setup-args)
                                                   (-> e
                                                       .-target
                                                       .-value)))}]

      [new-filters]]

     #_[old-fixed-filters
        {:task-types   task-types
         :named-views  named-views
         :current-view current-view
         :load-view!   load-view!}]

     [:div
      {:class "flex items-center gap-3"}


      [button/sm-primary-solid
       {:on-click (fn [_e]
                    (modals/open-modal! activity-entity-editor/modal-key
                                        {:new-entity-type :entity.type/task}))}
       [:i.fal.fa-plus]
       (loc/tr :task-add-button)]


      [:div
       [:div
        {:class "flex gap-2"}
        [button/square-secondary-open
         {:on-click #(swap! state-atom assoc :mode :kanban)}
         [:i.fal.fa-compress-alt]]

        [button/square-secondary-open
         {:on-click #(swap! state-atom assoc :mode :grid)}
         [:i.fal.fa-table]]]]

      (when (= view-mode :kanban)
        [:div
         [three-dots-dropdown]])]]))

(defn tasks-kanban
  [{:keys [tasks-override view-configuration]}]
  (let [has-data? (< 0 (count tasks-override))]
    [:div {:style {:height "100%"}}
     (if-not has-data?
       [no-results-view/no-entities-found-view]
       [:div {:style {:height "100%"}}
        (r/as-element
         [:> js/ReactVirtualized.AutoSizer
          (fn [m]
            (let [autosizer-height (aget m "height")
                  autosizer-width (aget m "width")
                  kanban-tasks @(rf/subscribe
                                 [::subs/tasks-kanban
                                  {:tasks-override tasks-override}]                                 ; Todo DELETE once real architecture is implemented
                                 )

                  card-view-config (:card-view view-configuration)

                  card-width (some-> card-view-config
                                     :card-settings
                                     :look-and-feel
                                     :card-width)

                  card-margin 16
                  column-width (+ (get-card-width card-width)
                                  (* card-margin 2))]
              (r/as-element
               (into
                [kanban/view
                 {:kanban-id          :kanban-1
                  :view-configuration card-view-config
                  :card-renderer      (fn [args]
                                        [:div
                                         {:style {:padding (str "10px " card-margin "px " "10px " card-margin "px")}}
                                         [task-kanban-card args (:card-settings card-view-config)]])
                  :column-renderer    (partial kanban/default-column-renderer
                                               {:data   kanban-tasks
                                                :config card-view-config})
                  :column-count       (-> kanban-tasks
                                          :column-labels
                                          count)
                  :data               kanban-tasks
                  :background-color   ""
                  :board-height       autosizer-height
                  :board-width        autosizer-width
                  :column-width       column-width
                  :column-gap         12
                  :on-drag-end        (fn [{:keys [item-id source-col-index target-col-index target-col-id]}]
                                        (when (and target-col-index (not= source-col-index target-col-index))
                                          (rf/dispatch [::events/drag-end
                                                        {:item-id                 item-id
                                                         :source                  source-col-index
                                                         :target                  target-col-index
                                                         :target-id               target-col-id
                                                         :card-view-configuration card-view-config}])))
                  }]))))])])]))


(defn recalculate-sorted-grid-entities
  "This is a quick fix for filtering and sorting in the new tasks kanban view.
   Copied from ns frontend.react.virtualized.scrollable-grid.common
   and adjusted a little"
  [{:keys [re-sort? state view-settings-state
           custom-filters custom-sort-fn
           visible-keys-and-schemas-fn]
    :as   setup-args}
   tasks]
  (let [tenant @session/tenant
        tenant-schema @session/tenant-schema

        filters-state (r/atom {})

        js-grid-entities (into-array tasks)

        visible-keys-and-schemas (when visible-keys-and-schemas-fn
                                   (visible-keys-and-schemas-fn))

        sort-by-key (or (:sort-by (when view-settings-state @view-settings-state))
                        :common/created-on)
        secondary-sort-by-key (or (:secondary-sort-by (when view-settings-state
                                                        @view-settings-state))
                                  :common/lower-case-str)
        sort-direction (or (:sort-direction (when view-settings-state
                                              @view-settings-state))
                           "DESC")
        secondary-sort-direction (or (:secondary-sort-direction (when view-settings-state
                                                                  @view-settings-state))
                                     "DESC")

        filters (when filters-state @filters-state)
        js-filtered-grid-entities (grid/js-filter-grid-entities js-grid-entities
                                                                (if custom-filters
                                                                  (concat custom-filters filters)
                                                                  filters))

        name-search-text (some->> @state
                                  :search-name
                                  :input
                                  (cu/str-trim-lcase))
        js-name-search-filtered (if-not (string/blank? name-search-text)
                                  (grid/js-name-search-filter name-search-text js-filtered-grid-entities)
                                  js-filtered-grid-entities)

        universal-search-text (some->> @state
                                       :search
                                       :input)
        js-universal-search-filtered (if-not (string/blank? universal-search-text)
                                       (grid/js-universal-search-filter (assoc setup-args
                                                                          :visible-keys-and-schemas
                                                                          visible-keys-and-schemas)
                                                                        universal-search-text
                                                                        js-name-search-filtered)
                                       js-name-search-filtered)

        length (.-length js-universal-search-filtered)
        js-non-blanks (array)
        js-blanks (array)
        _ (dotimes [index length]
            (let [entity (aget js-universal-search-filtered index)
                  val (fu/get-value tenant tenant-schema entity sort-by-key)
                  empty-value? (or (nil? val)
                                   (and (string? val) (string/blank? val))
                                   (and (coll? val) (empty? val)))]
              (if empty-value?
                (.push js-blanks entity)
                (.push js-non-blanks entity))))

        previous-sort-orders (:previous-sort-orders @state)
        result
        ^js/Array
        (if (and previous-sort-orders (not re-sort?))
          (-> js-universal-search-filtered
              (.sort (fn [e1 e2]
                       (compare (get previous-sort-orders (:db/id e1))
                                (get previous-sort-orders (:db/id e2))))))
          (let [js-sorted-grid-entities (if custom-sort-fn
                                          (into-array (custom-sort-fn js-non-blanks sort-by-key))
                                          (grid/js-sort-grid-entities js-non-blanks
                                                                      sort-by-key
                                                                      secondary-sort-by-key
                                                                      secondary-sort-direction))
                js-sorted-blanks (if custom-sort-fn
                                   (into-array (custom-sort-fn js-blanks sort-by-key))
                                   (grid/js-sort-grid-entities js-blanks
                                                               sort-by-key
                                                               secondary-sort-by-key
                                                               secondary-sort-direction))]
            (if (= "DESC" sort-direction)
              (.concat (.reverse js-sorted-grid-entities) js-sorted-blanks)
              (.concat js-sorted-grid-entities js-sorted-blanks))))

        render-data result

        render-data (into [] render-data)]
    render-data))


(defn render
  [grid-store kanban-feature?]
  (let [hover-atom (r/atom {:hover {:col nil :row nil}})

        entity-type :entity.type/task

        ; Todo this state away from local state, to more persistent location
        state-atom (r/atom {:mode (if kanban-feature?
                                    :kanban
                                    :grid)})

        ;;for saving grid references:
        recompute-grid-sizes! (fn []
                                (js/setTimeout
                                 (partial vu/recompute-grid-sizes-with-state! grid-store)
                                 0))]
    (fn [_args]
      (let [selected (:selected @db/tasks)
            filters @db/task-filters

            capman-not-admin? (and (tenant-customizations/capman?)
                                   (not (session/is-admin?)))

            passes-new-kanban-filter (fn [task filter]
                                       (let [{:keys [field value]} filter]
                                         (if (= field :task/completed?)
                                           (let [task-value (:task/completed? task)]
                                             (or (and (not task-value) (not value))
                                                 (and value task-value)))
                                           ; This must be task assignee then
                                           (= (get task field) value))))

            sorted-grid-entities (if kanban-feature?
                                   (cond->> @session/all-tasks
                                            true
                                            (recalculate-sorted-grid-entities
                                             (assoc (events/setup-args) :re-sort? true))
                                            capman-not-admin?
                                            (filterv (fn [task]
                                                       (= (:task/assignee task)
                                                          @session/account-id)))
                                            true
                                            (filterv (fn [task]
                                                       (let [filters @(rf/subscribe [::subs/task-filters])]
                                                         (if (not filters)
                                                           true
                                                           (condp = (count filters)
                                                             0 true

                                                             1 (passes-new-kanban-filter task (first filters))

                                                             2 (let [first-filter (first filters)
                                                                     last-filter (last filters)]
                                                                 (and (passes-new-kanban-filter task first-filter)
                                                                      (passes-new-kanban-filter task last-filter)))
                                                             (do
                                                               (js/console.warn "Unsupported amount of filters")
                                                               false)))))))

                                   (:sorted-grid-entities @db/tasks))

            setup-args (events/setup-args)
            modal-atom (:modal-state setup-args)
            show-bulk-edit-modal? (:bulk-edit-modal @modal-atom)

            ; Todo this is hacky way for kanban v1 release. Fix later!!
            view-configuration @(rf/subscribe [::subs/view-configuration])

            grid-atom (create-props-atom selected
                                         sorted-grid-entities
                                         filters
                                         grid-store
                                         recompute-grid-sizes!
                                         hover-atom
                                         modal-atom)

            named-views-minimized? (:named-views-minimized? @db/tasks)]
        [:div.grid-view
         (when-not kanban-feature?
           [named-views/view
            {:minimized?       named-views-minimized?
             :named-views      (events/named-views)
             :current-view     @db/view-settings
             :load-view!       (fn [named-view]
                                 (events/load-view! named-view)
                                 (grid/clear-selection! {:state db/tasks}))
             :toggle-minimize! (fn []
                                 (events/toggle-named-views!)
                                 (.setTimeout js/window recompute-grid-sizes! 1000))
             :class-name       "named-views-sidenav--tasks-view"}])
         [:div
          {:class-name (fu/cs "grid-view-content flex flex-col"
                              (when named-views-minimized? "named-views-minimized"))}

          (when show-bulk-edit-modal?
            [bulk-edit-modal/view
             {:setup-args   (events/setup-args)
              :entity-type  entity-type
              :close-modal! #(swap! modal-atom dissoc :bulk-edit-modal)}])

          (when kanban-feature?
            [new-toolbar
             {:state-atom   state-atom
              :named-views  (events/named-views)
              :current-view @db/view-settings
              :load-view!   (fn [named-view]
                              (events/load-view! named-view)
                              (grid/clear-selection! {:state db/tasks}))}])

          [:div
           {:style {:flex-grow  "2"}}
           (condp = (:mode @state-atom)
             :kanban [tasks-kanban
                      ; Todo delete tasks-override once real Re-Frame architecture is implemented
                      {:tasks-override     sorted-grid-entities
                       :view-configuration view-configuration}]
             :grid [scrollable-grid/view
                    {:props-atom grid-atom
                     :hover-atom hover-atom}])]
          ;; Curtains
          [webentry-resolution-curtain/view
           {:webentry-task     @db/webentry-task
            :resolve-webentry! events/resolve-webentry!}]]]))))

(defn view
  [args kanban-feature?]
  (let [grid-store (atom {})
        close-atom (atom false)
        resizer (fn []
                  (js/setTimeout (partial vu/recompute-grid-sizes-with-state! grid-store) 100))
        view-config-initialized? @(rf/subscribe [::subs/view-config-initialized?])]
    (when (and kanban-feature? (not view-config-initialized?))
      (rf/dispatch-sync [::events/start-tasks]))
    (.addEventListener js/window "resize" resizer)
    (r/create-class
     {:component-did-mount    (fn []
                                (fu/timeout 10
                                            (partial events/setup
                                                     close-atom
                                                     (or (keyword (:filter args))
                                                         (:named-view/key @db/tasks))))
                                (fu/timeout 10
                                            (fn []
                                              (rf/dispatch [::events/set-view-configuration-tracker])))

                                (when (nil? (:tasks-view @db/tasks))
                                  (let [initial-view (get args :tasks-view :task.type/simple)]
                                    (fu/timeout 500
                                                (fn []
                                                  (events/set-tasks-view initial-view))))))
      :component-will-unmount (fn []
                                (reset! close-atom true)
                                (events/cleanup)
                                (.removeEventListener js/window "resize" resizer))
      :reagent-render         (partial render grid-store kanban-feature?)})))

(defn new-view
  [route-params tasks-view]
  [view
   (merge route-params
          (when tasks-view
            {:tasks-view tasks-view})
          (when filter
            {:filter filter}))
   true])

(defn old-view
  [route-params tasks-view]
  [:<>
   [zap-toolbar/view db/tasks navigation-elems
    (partial zap-toolbar/search-elems
             {:placeholder (loc/tr :search-for-a-task)
              :state       db/tasks
              :on-change   (fn [e]
                             (grid/set-universal-search! (events/setup-args)
                                                         (-> e
                                                             .-target
                                                             .-value)))})
    (partial toolbar-actions)]
   [view
    (merge route-params
           (when tasks-view
             {:tasks-view tasks-view})
           (when filter
             {:filter filter}))
    false]])

(defmethod routing/render-view :tasks-grid
  [{:keys [view-params route-params]}]
  (let [tasks-view (:tasks-view view-params)
        tasks-kanban-feature? (session/has-feature-access? :feature/tasks-kanban)]
    (cond
      (or (session/is-read-only-user?)
          (not (session/tasks-access?)))
      [not-found/not-found-view]

      (session/new-grid-2023?)
      [default-page-layout/view
       [:<>
        [task-listing/page-content]
        ;; A webentry task from before the v3 resolution page opens this curtain.
        [webentry-resolution-curtain/view
         {:webentry-task     @db/webentry-task
          :resolve-webentry! events/resolve-webentry!}]]]

      :else
      [default-page-layout/view
       [:<>
        (if tasks-kanban-feature?
          [new-view]
          [old-view route-params tasks-view])]])))
